import { ChangesDetection } from "../../../../change-detection";
import { C_PatientLocation } from "../../patient/domain/patient-location.model";
import { I_AdmissionResponse } from "../api/admission.response";
import { C_Person } from "../../patient/domain/person.model";
import { distinctUntilChanged, filter, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";
import { E_ChangeAction } from "../../../../../enums/change-action.enum";
import { I_OptionItemResponse } from "../../../../unit/items/master-lists/api/option-list.response";
import { C_OptionItem } from "../../../../unit/items/master-lists/domain/option-list.model";

export class C_AdmissionList extends ChangesDetection<T_AdmissionListChange> {

  private newList: Map<string, C_AdmissionItem> = new Map();
  private cachedSubs = new Map<string, Observable<C_AdmissionItem | undefined>>();


  constructor(json?: I_AdmissionResponse) {
    super();
    if (!json) return;
    this.addNewAdmission(json);
  }

  size() {
    return this.newList.size;
  }

  getValues(): C_AdmissionItem[] {
    return Array.from(this.newList.values());
  }

  getAdmission(id: string): C_AdmissionItem | undefined {
    return this.newList.get(id);
  }

  addNewAdmission(a: I_AdmissionResponse) {
    if (!a.id) return;
    if (this.newList.has(a.id)) {
      this.updateAdmission(a);
      return;
    }

    const newAdmission = new C_AdmissionItem(a);
    this.newList.set(a.id, newAdmission);
    this.forceUpdateDetection({
      action: E_ChangeAction.Add,
      admissionId: a.id,
      data: newAdmission
    });
  }
  updateAdmission(a: I_AdmissionResponse) {
    if (!a.id) return;
    const admission = this.newList.get(a.id);
    if (!admission) {
      const newAdmission = new C_AdmissionItem(a);
      this.newList.set(a.id, newAdmission);
      this.forceUpdateDetection({
        action: E_ChangeAction.Add,
        admissionId: a.id,
        data: newAdmission
      });
      return;
    }

    admission.initializeFromJson(a);
    this.forceUpdateDetection({
      action: E_ChangeAction.Update,
      admissionId: a.id,
      data: a
    });
  }
  deleteAdmission(a: I_AdmissionResponse) {
    if (!a.id) return;
    if (this.newList.has(a.id)) {
      this.newList.delete(a.id);
      this.forceUpdateDetection({
        action: E_ChangeAction.Delete,
        admissionId: a.id
      });
    }
  }

  /**
   * Carga o actualización masiva inyectando un Set de IDs afectados
   */
  setAdmissionsBulk(admissionList: I_AdmissionResponse[]) {
    const affected = new Set<string>();

    admissionList.forEach(a => {
      if (!a.id) return;
      affected.add(a.id);

      const current = this.newList.get(a.id);
      if (current) {
        current.initializeFromJson(a);
      } else {
        this.newList.set(a.id, new C_AdmissionItem(a));
      }
    });

    this.forceUpdateDetection({ action: E_ChangeAction.AddBulk, affected });
  }

  resetAdmission() {
    this.newList.clear();
    this.forceUpdateDetection({ action: E_ChangeAction.Reset });
  }

  /**
     * Obtiene el flujo reactivo optimizado por ID de admisión
     */
  getLiveAdmission(admissionId: string): Observable<C_AdmissionItem | undefined> {
    const cacheKey = admissionId;
    if (this.cachedSubs.has(cacheKey)) return this.cachedSubs.get(cacheKey)!;

    const stream$ = this.getForcedChanges().pipe(
      startWith(null),
      filter((change) => {
        if (!change) return true; // Disparo inicial
        if (change.action === E_ChangeAction.Reset) return true;
        if (change.action === E_ChangeAction.AddBulk) return change.affected.has(admissionId);

        return change.admissionId === admissionId;
      }),
      map(() => this.newList.get(admissionId)),
      distinctUntilChanged(),
      switchMap(item => {
        if (!item) return of(undefined);

        // Escucha cambios internos de propiedades dentro del ítem de admisión
        return item.getForcedChanges().pipe(
          startWith(item),
          map(() => item)
        );
      }),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.cachedSubs.set(cacheKey, stream$);
    return stream$;
  }

  clearSubs() {
    this.cachedSubs.clear();
    this.forceUpdateDetection({ action: E_ChangeAction.Reset });
  }

}

export class C_AdmissionItem extends ChangesDetection {
  id!: string
  admissionDate?: Date;
  nhc?: string
  unitId?: string
  pointOfCareId?: string | null;
  person?: C_Person;
  origin?: C_OptionItem;
  diagnosis?: C_OptionItem;
  insulation?: C_OptionItem;
  mobility?: C_OptionItem;
  passiveSitting?: C_OptionItem;
  languageBarrier?: C_OptionItem[];
  allergies?: C_OptionItem[];
  patientLocation?: C_PatientLocation;
  hasData: boolean = false;
  originAux?: string;
  color?: string;
  diagnosisAux?: string;
  class?: string;
  data?: string[];
  repeated?: boolean;

  constructor(json?: I_AdmissionResponse) {
    super();
    if (!json) return;
    this.initializeFromJson(json);
  }

  initializeFromJson(info?: I_AdmissionResponse) {
    this.hasData = true;
    if (!info) return;
    if (info.id != null) {
      this.id = info.id!;
    }
    if (info.admissionDate != null) {
      this.admissionDate = new Date(info.admissionDate);
    }
    this.nhc = info.nhc
    this.unitId = info.unitId
    this.pointOfCareId = info.pointOfCareId
    if (info.person != null) {
      this.person ? this.person?.initialize(info.person) : this.person = new C_Person(info.person);
    }
    if (info.origin) {
      this.origin ? this.origin?.initializeFromJson(info.origin) : this.origin = new C_OptionItem(info.origin);
    }
    if (info.diagnosis != null) {
      this.diagnosis ? this.diagnosis?.initializeFromJson(info.diagnosis) : this.diagnosis = new C_OptionItem(info.diagnosis);
    }
    if (info.insulation != null) {
      this.insulation ? this.insulation?.initializeFromJson(info.insulation) : this.insulation = new C_OptionItem(info.insulation);
    }
    if (info.mobility != null) {
      this.mobility ? this.mobility?.initializeFromJson(info.mobility) : this.mobility = new C_OptionItem(info.mobility);
    }
    if (info.passiveSitting != null) {
      this.passiveSitting ? this.passiveSitting?.initializeFromJson(info.passiveSitting) : this.passiveSitting = new C_OptionItem(info.passiveSitting);
    }
    // if (info.languageBarrier != null) {
    //   this.languageBarrier ? this.languageBarrier?.initializeFromJson(info.languageBarrier) : this.languageBarrier = new C_OptionItem(info.languageBarrier);
    // } else {
    //   this.languageBarrier = info.languageBarrier
    // }
    try {
      let lang: I_OptionItemResponse[] | undefined = info.languageBarrier;
      if (!Array.isArray(info.languageBarrier) && info.languageBarrier) lang = [info.languageBarrier];
      this.languageBarrier = lang?.map(a => new C_OptionItem(a)) ?? [];
    } catch (error) {
      console.log("error setteando language barrier", info.languageBarrier);
    }
    this.allergies = info.allergies?.map(a => new C_OptionItem(a)) ?? [];
    if (info.hasOwnProperty('patientLocation')) {
      this.patientLocation ? this.patientLocation?.initialize(info.patientLocation) : this.patientLocation = new C_PatientLocation(info.patientLocation);
    }
    this.originAux = info.originAux
    this.diagnosisAux = info.diagnosisAux
    if (info.hasOwnProperty('class')) this.class = (info as C_AdmissionItem).class;
    if (info.hasOwnProperty('data')) this.data = (info as C_AdmissionItem).data;
    if (info.hasOwnProperty('repeated')) this.repeated = (info as C_AdmissionItem).repeated;
    this.forceUpdateDetection();
  }

  clone(): C_AdmissionItem {
    const clone = new C_AdmissionItem();
    clone.id = this.id;
    clone.admissionDate = this.admissionDate;
    clone.nhc = this.nhc;
    clone.unitId = this.unitId;
    clone.pointOfCareId = this.pointOfCareId;
    if (this.person) clone.person = this.person.clone();
    if (this.origin) clone.origin = this.origin;
    if (this.diagnosis) clone.diagnosis = this.diagnosis;
    if (this.allergies) clone.allergies = [...this.allergies];
    if (this.insulation) clone.insulation = this.insulation;
    if (this.mobility) clone.mobility = this.mobility;
    if (this.passiveSitting) clone.passiveSitting = this.passiveSitting;
    if (this.languageBarrier) clone.languageBarrier = this.languageBarrier;
    clone.originAux = this.originAux;
    clone.diagnosisAux = this.diagnosisAux;
    if (this.patientLocation) clone.patientLocation = this.patientLocation?.clone();
    clone.color = this.color;
    clone.class = this.class;
    if (this.data) clone.data = [...this.data];
    clone.repeated = this.repeated;
    clone.hasData = this.hasData;
    return clone;
  }

  toInterface(): I_AdmissionResponse {
    return {
      id: this.id,
      admissionDate: this.admissionDate?.toISOString(),
      nhc: this.nhc ?? '',
      unitId: this.unitId ?? '',
      pointOfCareId: this.pointOfCareId,
      person: this.person?.toInterface() ?? {},
      origin: this.origin?.toInterface(),
      diagnosis: this.diagnosis?.toInterface(),
      insulation: this.insulation?.toInterface(),
      mobility: this.mobility?.toInterface(),
      passiveSitting: this.passiveSitting?.toInterface(),
      // languageBarrier: this.languageBarrier?.toInterface(),
      languageBarrier: this.languageBarrier?.map(a => a.toInterface()),
      allergies: this.allergies?.map(a => a.toInterface()),
      patientLocation: this.patientLocation?.toInterface(),
      originAux: this.originAux,
      diagnosisAux: this.diagnosisAux
    }
  }

}

export type T_AdmissionListChange =
  | { action: E_ChangeAction.Add; admissionId: string; data: C_AdmissionItem }
  | { action: E_ChangeAction.Update; admissionId: string; data: I_AdmissionResponse }
  | { action: E_ChangeAction.Delete; admissionId: string }
  | { action: E_ChangeAction.AddBulk; affected: Set<string> }
  | { action: E_ChangeAction.Reset };